import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import "../styles/HomePageModern.css";
import site_logo from "../assets/site_logo.png";
import telemetryExampleImg from "../assets/images/small-telemetry-example-mx5.png";
import { getIconFileName } from "../utils/iconUtils";
import { seriesList } from "../utils/seriesList";
import { fetchHomepageLapTimes } from "../utils/homepageLapTimes";
import { carColors } from "../utils/carColors";
import CategoryIcon from "./CategoryIcon";
import { CountdownTimer } from "./ContestsPage";
import Plot from "react-plotly.js";
import { maskDriverName, isVisibleDriver, authHeaders } from "../utils/driverPrivacy";
import IracingPrivacyNotice from "./IracingPrivacyNotice";
import HomeProfileCard from "./HomeProfileCard";
import { CHART, chartAxis, chartBase, chartLegend, chartWatermark } from "../utils/chartTheme";
import { CategoryFilter, LoadingOverlay, PanelHeader, SegmentedControl } from "./RefreshUI";

const homepageSeries = [
        seriesList.find(name => name.startsWith("GT3 Challenge Fixed by Fanatec -")),
        seriesList.find(name => name.startsWith("GT4 Falken Tyre Challenge -")),
        seriesList.find(name => name.startsWith("Production Car Challenge by Sim-Lab -")),
        seriesList.find(name => name.startsWith("IMSA iRacing Series") && !name.includes("Fixed")),
];

// Remove transparent asset margins so every selector logo has the same visible height.
const HomepageSeriesIcon = ({ src, label, height = 2.5 }) => {
        const [bounds, setBounds] = useState(null);
        useEffect(() => {
                let cancelled = false;
                setBounds(null);
                const image = new Image();
                image.onload = () => {
                        if (cancelled) return;
                        try {
                                const canvas = document.createElement("canvas");
                                canvas.width = image.naturalWidth;
                                canvas.height = image.naturalHeight;
                                const context = canvas.getContext("2d");
                                context.drawImage(image, 0, 0);
                                const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
                                let left = canvas.width, top = canvas.height, right = -1, bottom = -1;
                                for (let y = 0; y < canvas.height; y++) {
                                        for (let x = 0; x < canvas.width; x++) {
                                                if (data[(y * canvas.width + x) * 4 + 3] > 10) {
                                                        left = Math.min(left, x); right = Math.max(right, x);
                                                        top = Math.min(top, y); bottom = Math.max(bottom, y);
                                                }
                                        }
                                }
                                if (right >= left) setBounds({ left, top, width: right - left + 1, height: bottom - top + 1, imageWidth: canvas.width, imageHeight: canvas.height });
                        } catch (_) {
                                // Keep the original image if pixel access is unavailable.
                        }
                };
                image.src = src;
                return () => { cancelled = true; };
        }, [src]);
        if (!bounds) return <img src={src} alt={label} style={{ width: "100%", height: `${height}rem`, objectFit: "contain" }} />;
        return (
                <svg role="img" aria-label={label} viewBox={`${bounds.left} ${bounds.top} ${bounds.width} ${bounds.height}`}
                        style={{ height: `${height}rem`, width: `${height * bounds.width / bounds.height}rem`, maxWidth: "100%", display: "block" }}>
                        <image href={src} width={bounds.imageWidth} height={bounds.imageHeight} />
                </svg>
        );
};

const TelemetryFeatureModal = ({ isOpen, onClose }) => {
        if (!isOpen) return null;

        return (
                <div className="telemetry-modal-overlay" onClick={onClose}>
                        <div className="telemetry-modal-content" onClick={(e) => e.stopPropagation()}>
                                <button className="telemetry-modal-x-button" onClick={onClose}>
                                        &times;
                                </button>
                                <h2 className="telemetry-modal-title">New: Lap Comparison & Telemetry Analysis</h2>
                                <p className="telemetry-modal-subtitle">Compare your laps against the fastest drivers and improve your racing line</p>

                                <img src={telemetryExampleImg} alt="Telemetry comparison example" className="telemetry-modal-image" />

                                <div className="telemetry-modal-features">
                                        <div className="telemetry-feature-item">
                                                <span className="feature-icon">&bull;</span>
                                                <div className="feature-text">
                                                        <strong>Racing Line Comparison</strong>
                                                        <p>See exactly where you're losing time vs the fastest lap</p>
                                                </div>
                                        </div>
                                        <div className="telemetry-feature-item">
                                                <span className="feature-icon">&bull;</span>
                                                <div className="feature-text">
                                                        <strong>Speed & Input Traces</strong>
                                                        <p>Analyze throttle, brake, and steering inputs side-by-side</p>
                                                </div>
                                        </div>
                                        <div className="telemetry-feature-item">
                                                <span className="feature-icon">&bull;</span>
                                                <div className="feature-text">
                                                        <strong>Stats Comparison</strong>
                                                        <p>Compare max speed, full throttle %, and track conditions</p>
                                                </div>
                                        </div>
                                </div>

                                <Link to="/telemetry" className="telemetry-modal-cta" onClick={onClose}>
                                        Head to the Telemetry page to start comparing your laps!
                                </Link>

                                <button className="telemetry-modal-close-button" onClick={onClose}>
                                        Got it!
                                </button>
                        </div>
                </div>
        );
};

const CATEGORIES = ["sports_car", "formula_car", "oval", "dirt_road", "dirt_oval"];

const CATEGORY_LABELS = {
        sports_car: "Sports car",
        formula_car: "Formula car",
        oval: "Oval",
        dirt_road: "Dirt road",
        dirt_oval: "Dirt oval",
};

// The iRating distribution endpoint uses PascalCase category keys.
const IRATING_CATEGORY_KEYS = {
        sports_car: "SportsCar",
        formula_car: "FormulaCar",
        oval: "Oval",
        dirt_road: "DirtRoad",
        dirt_oval: "DirtOval",
};

const trimSafetyName = (name) => name.replace("2024 Season 4", "").replace(/-/g, " ").replace(/\s+/g, " ").trim();

const formatContestTime = (value) => {
        const mins = Math.floor(value / 60);
        const secs = (value % 60).toFixed(3);
        return `${mins}:${secs.padStart(6, "0")}`;
};

export const HomePage = () => {
        const [showTelemetryModal, setShowTelemetryModal] = useState(false);
        const navigate = useNavigate();
        const [recentSessionsLoading, setRecentSessionsLoading] = useState(true);
        const [recentSessions, setRecentSessions] = useState([]);
        const [recentSearches, setRecentSearches] = useState([]);
        const [timeSinceArray, setTimeSinceArray] = useState([]);
        const [dataSP, setDataSP] = useState([]);
        const [dataSS, setDataSS] = useState([]);
        const [dataSG, setDataSG] = useState([]);
        const [lapLoading, setLapLoading] = useState(true);
        const [dataTypeChoice, setDataTypeChoice] = useState("SP");
        const [graphSeriesChoice, setGraphSeriesChoice] = useState(homepageSeries[0]);
        const [page, setPage] = useState(0);
        const itemsPerPage = window.innerWidth > 800 ? 9 : 8;
        const maxY = Math.max(...dataSG.flatMap((item) => item.best_lap_time_avg / 10000));
        const [multiclassHeightModifier, setMulticlassHeightModifier] = useState(0);
        const [chart1isLoading, setChart1isLoading] = useState(true);
        const [iRatingData, setIRatingData] = useState([]);
        const [iRatingLoading, setIRatingLoading] = useState(true);
        const [iRatingCategory, setIRatingCategory] = useState("SportsCar");
        const [topIRatings, setTopIRatings] = useState([]);
        const [topIRatingsLoading, setTopIRatingsLoading] = useState(true);
        const [topIRatingsCategory, setTopIRatingsCategory] = useState("combined");
        const [activeContest, setActiveContest] = useState(null);
        const [contestLoading, setContestLoading] = useState(true);

        useEffect(() => {
                const fetchData = async () => {
                        setChart1isLoading(true);
                        try {
                                const result = await axios("https://iracing6-backend.herokuapp.com/api/sof-results/sofresults");

                                // convert each item's start_time to hour of day in UTC
                                result.data.forEach((item) => {
                                        const startTime = new Date(item.start_time);
                                        item.start_time = startTime.getUTCHours();
                                });
                                setDataSP(result.data);
                                setChart1isLoading(false);
                        } catch (error) {
                                console.error("Error fetching data: ", error);
                        }
                };

                fetchData();
        }, []);

        // fetch from `https://iracing6-backend.herokuapp.com/api/series-avg-lap-times/average-lap-time/${graphSeriesChoice}`
        useEffect(() => {
                const controller = new AbortController();
                setDataSG([]);
                setLapLoading(true);
                const fetchData = async () => {
                        try {
                                const json = await fetchHomepageLapTimes(graphSeriesChoice, controller.signal);
                                if (controller.signal.aborted) return;
                                if (json.length === 0) {
                                        setDataSG([]);
                                        return;
                                }

                                const lapTimes = json.map((item) => item.best_lap_time_avg / 10000);
                                const Q1 = lapTimes.sort((a, b) => a - b)[Math.floor(lapTimes.length / 4)];
                                const Q3 = lapTimes.sort((a, b) => a - b)[Math.floor((3 * lapTimes.length) / 4)];
                                const IQR = Q3 - Q1;
                                const lowerBound = Q1 - 2 * IQR;
                                const upperBound = Q3 + 2 * IQR;

                                const filteredData = json.filter((item) => {
                                        const lapTime = item.best_lap_time_avg / 10000;
                                        return lapTime >= lowerBound && lapTime <= upperBound;
                                });

                                const sortedData = filteredData.sort((a, b) => a.i_rating_range[0] - b.i_rating_range[0]);

                                setDataSG(sortedData);
                        } catch (error) {
                                if (controller.signal.aborted) return;
                                console.error("Error fetching data: ", error);
                                setDataSG([]);
                        } finally {
                                if (!controller.signal.aborted) setLapLoading(false);
                        }
                };
                fetchData();
                return () => controller.abort();
        }, [graphSeriesChoice]);

        useEffect(() => {
                const fetchDataSS = async () => {
                        try {
                                const response = await fetch("https://iracing6-backend.herokuapp.com/api/series-safety/");
                                const data = await response.json();
                                data.sort((a, b) => b.totalCorners / b.totalIncidents - a.totalCorners / a.totalIncidents);
                                // limit data to 10 items
                                data.splice(10, data.length - 10);
                                setDataSS(data);
                        } catch (error) {
                                console.error("Error fetching data: ", error);
                        }
                };

                fetchDataSS();
        }, []);

        // fetch the 3 most recent sessions
        useEffect(() => {
                const fetchRecentSessions = async () => {
                        try {
                                const response = await axios.get(`https://iracing6-backend.herokuapp.com/api/sessionData/resultsrecent?limit=50`);
                                setRecentSessions(response.data);
                                setRecentSessionsLoading(false);
                        } catch (error) {
                                console.error("Error fetching recent sessions:", error);
                        }
                };
                fetchRecentSessions();
        }, []);

        // fetch the wr recent searches
        useEffect(() => {
                const fetchRecentSearches = async () => {
                        try {
                                const response = await axios.get(`https://iracing6-backend.herokuapp.com/api/world-records/recent-searches`);
                                // flip the array
                                response.data.reverse();
                                // make sure that there are not 2 of the same car/track combos
                                const seen = new Set();
                                response.data = response.data.filter((item) => {
                                        const key = item.carId + "-" + item.trackId;
                                        return seen.has(key) ? false : seen.add(key);
                                });
                                // Try bulk fetch of cars/tracks, then fall back to per-id lookups for first 3
                                let carMap = new Map();
                                let trackMap = new Map();
                                try {
                                        const [carsResp, tracksResp] = await Promise.all([
                                                axios.get("https://iracing6-backend.herokuapp.com/api/car-basic-info/"),
                                                axios.get("https://iracing6-backend.herokuapp.com/api/track-basic-info/"),
                                        ]);
                                        const cars = Array.isArray(carsResp.data) ? carsResp.data : [];
                                        const tracks = Array.isArray(tracksResp.data) ? tracksResp.data : [];
                                        carMap = new Map(cars.map((c) => [c.car_id, c.car_name]));
                                        trackMap = new Map(tracks.map((t) => [t.track_id, t.track_name]));
                                } catch (e) {
                                        // Bulk endpoints may be unavailable; proceed to per-id fetch for visible items
                                }

                                let merged = response.data.map((item) => ({
                                        ...item,
                                        carName: item.carName && item.carName.trim() ? item.carName : carMap.get(item.carId),
                                        trackName: item.trackName && item.trackName.trim() ? item.trackName : trackMap.get(item.trackId),
                                }));

                                // Fallback: ensure first 3 have names by fetching per-id where missing
                                const firstThree = merged.slice(0, 3);
                                const fixedFirstThree = await Promise.all(
                                        firstThree.map(async (item) => {
                                                let carName = item.carName;
                                                let trackName = item.trackName;
                                                if (!carName) {
                                                        try {
                                                                const r = await axios.get(`https://iracing6-backend.herokuapp.com/api/car-basic-info/getName/${item.carId}`);
                                                                carName = typeof r.data === "string" ? r.data : carName;
                                                        } catch (e) {}
                                                }
                                                if (!trackName) {
                                                        try {
                                                                const r = await axios.get(`https://iracing6-backend.herokuapp.com/api/track-basic-info/getName/${item.trackId}`);
                                                                trackName = typeof r.data === "string" ? r.data : trackName;
                                                        } catch (e) {}
                                                }
                                                return { ...item, carName, trackName };
                                        }),
                                );
                                merged = [...fixedFirstThree, ...merged.slice(3)];

                                setRecentSearches(merged);
                        } catch (error) {
                                console.error("Error fetching recent sessions:", error);
                        }
                };
                fetchRecentSearches();
        }, []);

        // Fetch iRating distribution data
        useEffect(() => {
                const fetchIRatingData = async () => {
                        setIRatingLoading(true);
                        try {
                                const response = await axios.get(`https://iracing6-backend.herokuapp.com/api/irating-data/iratings${iRatingCategory}/1`);
                                setIRatingData(response.data);
                                setIRatingLoading(false);
                        } catch (error) {
                                console.error("Error fetching iRating data:", error);
                                setIRatingLoading(false);
                        }
                };
                fetchIRatingData();
        }, [iRatingCategory]);

        // Fetch top iRatings leaderboard data
        useEffect(() => {
                const fetchTopIRatings = async () => {
                        setTopIRatingsLoading(true);
                        try {
                                const response = await axios.get(`https://iracing6-backend.herokuapp.com/api/top-iratings/category/${topIRatingsCategory}`, { headers: authHeaders() });
                                setTopIRatings(response.data);
                                setTopIRatingsLoading(false);
                        } catch (error) {
                                console.error("Error fetching top iRatings:", error);
                                setTopIRatingsLoading(false);
                        }
                };
                fetchTopIRatings();
        }, [topIRatingsCategory]);

        // Fetch active contest for contest summary section
        useEffect(() => {
                const fetchActiveContest = async () => {
                        setContestLoading(true);
                        try {
                                const response = await axios.get("https://iracing6-backend.herokuapp.com/api/contests/active");
                                setActiveContest(Array.isArray(response.data) ? response.data[0] : response.data);
                        } catch (error) {
                                console.error("Error fetching active contest:", error);
                        }
                        setContestLoading(false);
                };
                fetchActiveContest();
        }, []);

        // Calculate time since the session started
        useEffect(() => {
                if (recentSessions.length > 0) {
                        const calculateTimeSince = (startTime) => {
                                const start = new Date(startTime).getTime();
                                const now = Date.now();
                                const differenceInMinutes = Math.floor((now - start) / 60000);
                                return differenceInMinutes;
                        };

                        const timeSinceArray = recentSessions.map((session) => calculateTimeSince(session.start_time));
                        setTimeSinceArray(timeSinceArray);
                }
        }, [recentSessions]);

        // Show telemetry feature modal for first-time visitors
        useEffect(() => {
                const hasSeenTelemetryFeatures = localStorage.getItem("hasSeenTelemetryFeatures");
                if (!hasSeenTelemetryFeatures) {
                        localStorage.setItem("hasSeenTelemetryFeatures", "true");
                        setShowTelemetryModal(true);
                }
        }, []);

        const handleCloseTelemetryModal = () => {
                setShowTelemetryModal(false);
        };

        const handleDataTypeChange = (type) => {
                setDataTypeChoice(type);
        };

        const handleViewMore = () => {
                if (dataTypeChoice === "SP") {
                        navigate("/charts/seriespopularity");
                } else {
                        navigate("/charts/seriessafety");
                }
        };

        const handleRecentSessionLink = (x) => {
                navigate(`/RaceResults/${recentSessions[x]?.subsession_id}`);
        };

        // Method to get the icon URL
        const getIconUrl = (seasonName) => {
                const iconName = getIconFileName(seasonName);
                // Assuming the icons are stored in the public folder under assets
                return `${process.env.PUBLIC_URL}/assets/series-icons/${iconName}.png`;
        };

        const groupedData = dataSG.reduce((acc, item) => {
                const className = item.compositeKey.carClassName;
                if (!acc[className]) {
                        acc[className] = [];
                }
                acc[className].push(item);
                return acc;
        }, {});

        const plotData = () => {
                const allXValues = dataSG.flatMap((item) => item.i_rating_range);
                const minX = Math.min(...allXValues);
                const maxX = Math.max(...allXValues);
                const xValues = [];
                for (let x = minX; x <= maxX; x += 100) {
                        xValues.push(x);
                }

                const plotLines = Object.keys(groupedData).map((className) => {
                        const classData = groupedData[className];
                        const yValues = xValues.map((x) => {
                                const item = classData.find((item) => x >= item.i_rating_range[0] && x <= item.i_rating_range[1]);
                                return item ? item.best_lap_time_avg / 10000 : null;
                        });

                        // Filter out leading and trailing null values to prevent gaps at the ends
                        const firstNonNullIndex = yValues.findIndex((value) => value !== null);
                        const lastNonNullIndex = yValues.length - 1 - [...yValues].reverse().findIndex((value) => value !== null);
                        const trimmedXValues = xValues.slice(firstNonNullIndex, lastNonNullIndex + 1);
                        const trimmedYValues = yValues.slice(firstNonNullIndex, lastNonNullIndex + 1);

                        return {
                                x: trimmedXValues,
                                y: trimmedYValues,
                                type: "scatter",
                                mode: "lines",
                                line: { width: 2, shape: "spline", smoothing: 0.6 },
                                marker: { color: carColors[className] || "#f1c40f" },
                                name: className,
                                connectgaps: true,
                                // fill: "tonexty", // Fill area below the line
                                fillcolor: `rgba(241, 196, 15, 0.2)`, // Gradient color (adjust opacity as needed)
                        };
                });

                // Check if there is only one line being plotted
                if (plotLines.length === 1) {
                        plotLines[0].line = { ...plotLines[0].line, color: "#f1c40f" };
                }

                return plotLines;
        };

        // Add this click handler function before your return statement
        const handlePlotClick = () => {
                // Navigate to the specific series lap time chart page
                navigate(`/charts/SeriesLapTimeChart/${encodeURIComponent(graphSeriesChoice)}`);
        };

        const handleIRatingGraphClick = () => {
                navigate("/charts/iRating");
        };

        // Group iRatings by 100-point ranges
        const groupIRatingsByRange = (iRatings) => {
                const groupedIRatings = {};

                iRatings.forEach((iRating) => {
                        const range = Math.floor(iRating / 100) * 100;
                        if (groupedIRatings[range]) {
                                groupedIRatings[range] += 1;
                        } else {
                                groupedIRatings[range] = 1;
                        }
                });

                return groupedIRatings;
        };

        const calculateAverageSessionPerSeason = () => {
                const seasonSessions = {};
                const sessionCounts = {};
                const sessionPopulation = {};

                const ignoredSeasons = ["NASCAR iRacing Series - 2023 Season Open", "NASCAR iRacing Series - 2023 Season Fixed", "eNASCAR Road To Pro Qual Series Round 1 - 2023 Season"];

                const filteredData = dataSP;

                filteredData.forEach((item) => {
                        const { season_name, session_id, population } = item;

                        // Ignore item if season_name is undefined
                        if (!season_name) {
                                return;
                        }

                        if (season_name in seasonSessions) {
                                seasonSessions[season_name].push(session_id);
                        } else {
                                seasonSessions[season_name] = [session_id];
                        }

                        if (session_id in sessionPopulation) {
                                sessionPopulation[session_id] += population;
                        } else {
                                sessionPopulation[session_id] = population;
                        }
                });

                for (let season in seasonSessions) {
                        const sessionIds = seasonSessions[season];
                        sessionCounts[season] = {};

                        sessionIds.forEach((sessionId) => {
                                if (sessionId in sessionCounts[season]) {
                                        sessionCounts[season][sessionId]++;
                                } else {
                                        sessionCounts[season][sessionId] = 1;
                                }
                        });
                }

                const averageRepetitionPerSeason = {};
                const averagePopulationPerSeason = {};
                for (let season in sessionCounts) {
                        const totalCount = Object.values(sessionCounts[season]).reduce((a, b) => a + b, 0);
                        averageRepetitionPerSeason[season] = totalCount / Object.keys(sessionCounts[season]).length;

                        let totalPopulation = 0;
                        for (let sessionId in sessionCounts[season]) {
                                totalPopulation += sessionPopulation[sessionId];
                        }
                        averagePopulationPerSeason[season] = totalPopulation / Object.keys(sessionCounts[season]).length;
                }

                return { averageRepetitionPerSeason, averagePopulationPerSeason };
        };

        const { averageRepetitionPerSeason, averagePopulationPerSeason } = calculateAverageSessionPerSeason();

        const seasonsData = Object.keys(averageRepetitionPerSeason).map((season) => ({
                season,
                averageRepetition: averageRepetitionPerSeason[season],
                averagePopulation: averagePopulationPerSeason[season],
        }));

        seasonsData.sort((a, b) => b.averagePopulation - a.averagePopulation);

        // trimming season names
        const pageData = {
                seasons: seasonsData.slice(page * itemsPerPage, (page + 1) * itemsPerPage).map((item) => item.season),
                repetitions: seasonsData.slice(page * itemsPerPage, (page + 1) * itemsPerPage).map((item) => item.averageRepetition),
                populations: seasonsData.slice(page * itemsPerPage, (page + 1) * itemsPerPage).map((item) => item.averagePopulation),
        };

        // Function to trim season names for display
        const displayTrimSeasonName = (seasonName) => {
                return seasonName
                        .replace("2024 Season 4", "") // Remove "2024 Season 4"
                        .replace(/-/g, " ") // Replace hyphens with spaces
                        .replace(/\s+/g, " ") // Replace multiple spaces with a single space
                        .replace(/Fixed Fixed/g, "Fixed") // Replace "Fixed Fixed" with "Fixed"
                        .trim(); // Trim leading and trailing spaces
        };

        // Create a separate array for Plotly with trimmed season names
        const plotlySeasonNames = pageData.seasons.map(displayTrimSeasonName);

        const formatTime = (seconds) => {
                const minutes = Math.floor(seconds / 60);
                const remainingSeconds = Math.floor(seconds % 60); // Remove milliseconds
                return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`; // Ensures MM:SS format
        };

        const yMaxCeil = Math.ceil(maxY);
        const minY = Math.min(...dataSG.map((item) => item.best_lap_time_avg / 10000));
        const yMinFloor = Math.floor(minY);
        const yRangeCountIfStep1 = yMaxCeil - yMinFloor + 1;
        const yTickStep = yRangeCountIfStep1 > 24 ? 4 : yRangeCountIfStep1 > 16 ? 2 : 1; // Use 4s if >24, 2s if >16, else 1s
        const yTickVals = [];
        for (let v = yMinFloor; v <= yMaxCeil; v += yTickStep) {
                yTickVals.push(v);
        }
        const yTickText = yTickVals.map(formatTime);

        const layoutSC = {
                ...chartBase(),
                hovermode: false,
                xaxis: {
                        ...chartAxis(),
                        title: { text: "iRating", font: { size: window.innerWidth < 1200 ? 12 : 13, color: CHART.muted }, standoff: window.innerWidth < 800 ? 8 : 12 },
                        tickformat: "~s",
                        tick0: 0,
                        dtick: 500,
                        showgrid: false,
                        automargin: true,
                },
                yaxis: {
                        ...chartAxis(),
                        title: { text: "Avg qualifying lap", font: { size: window.innerWidth < 1200 ? 12 : 13, color: CHART.muted } },
                        tickvals: yTickVals,
                        ticktext: yTickText,
                        ticklen: 0,
                        automargin: true,
                },
                // On phones the legend sits under the chart so long car names never collide with the lines.
                legend:
                        window.innerWidth > 800
                                ? chartLegend({ x: 0.99, y: 0.99, xanchor: "right", yanchor: "top" })
                                : { ...chartLegend({ x: 0, y: -0.3, xanchor: "left", yanchor: "top" }), bgcolor: "rgba(0,0,0,0)", borderwidth: 0 },
                showlegend: true,
                // Lines fall left to right, so bottom-left is empty on desktop; phones squeeze the lines into
                // every corner, so the watermark sits in the (taller) top margin instead.
                annotations: [window.innerWidth > 800 ? chartWatermark("bottom-left") : chartWatermark("top-right", { outside: true })],
                width: window.innerWidth > 800 ? 800 : 318,
                height: window.innerWidth > 800 ? 380 : Math.max(340, Math.floor((window.innerHeight - 600) * 1.15) + 60),
                margin: window.innerWidth > 800 ? { l: 64, r: 16, t: 8, b: 48 } : { l: 10, r: 10, t: 22, b: 100 },
        };

        // Match the fixed image proportions in the Series Popularity table,
        // enlarged by 50% for the homepage. Convert pixels to plot coordinates.
        const homeIconWidth = window.innerWidth > 800 ? 52.8 : 36;
        const homeIconHeight = window.innerWidth > 800 ? 36 : 26.4;
        // Left margin only needs room for the series icons (homeIconWidth + 8px gap).
        const homePopMargin = window.innerWidth > 800 ? { l: 72, r: 16, t: 6, b: 34 } : { l: window.innerWidth / 6.6, r: 10, t: 20, b: 34 };
        const homePlotWidth = (window.innerWidth > 800 ? 800 : 318) - homePopMargin.l - homePopMargin.r;
        const homePlotHeight = window.innerWidth > 800 ? 310 : Math.max(260, Math.floor((window.innerHeight - 420) * 0.85)) - 54;
        const homeRowCount = Math.max(1, pageData.seasons.length);
        const images = pageData.seasons.map((season, index) => ({
                source: `${process.env.PUBLIC_URL}/assets/series-icons/${getIconFileName(season)}.png`,
                xref: "paper",
                yref: "y",
                x: -(homeIconWidth / 2 + 8) / homePlotWidth,
                y: plotlySeasonNames[index],
                xanchor: "center",
                yanchor: "middle",
                sizex: homeIconWidth / homePlotWidth,
                sizey: homeIconHeight * homeRowCount / homePlotHeight,
                sizing: "contain",
                layer: "above",
        }));

        const recentWorldRecords = recentSearches.slice(0, 3).every((s) => s.carName && s.trackName) ? recentSearches.slice(0, 3) : [];

        return (
                <div className="hp">
                        {/* <TelemetryFeatureModal isOpen={showTelemetryModal} onClose={handleCloseTelemetryModal} /> */}
                        {/* <div className="homepage-banner">
                                <h1>Data will be frozen during week 13 to prepare for a seasonal update.</h1>
                        </div> */}
                        <div className="hp-brand">
                                <img className="hp-brand__logo" src={site_logo} alt="iRacing Data" />
                        </div>

                        <main className="hp-main">
                                <div className="hp-top">
                                        <HomeProfileCard />
                                        <Link to="/support" className="hp-support">
                                                <span className="hp-support__title">iRacingData Pro</span>
                                                <span className="hp-support__text">Ad-free site, Pro Dashboard and scheduled emails.</span>
                                        </Link>
                                </div>

                                <section className="hp-panel">
                                        <PanelHeader
                                                title={dataTypeChoice === "SP" ? "Series popularity" : "Series safety"}
                                                action={
                                                        <button type="button" className="hp-link" onClick={handleViewMore}>
                                                                See more
                                                        </button>
                                                }
                                        >
                                                <SegmentedControl
                                                        label="Series chart"
                                                        value={dataTypeChoice}
                                                        onChange={handleDataTypeChange}
                                                        options={[
                                                                { value: "SP", content: "Popularity" },
                                                                { value: "SS", content: "Safety" },
                                                        ]}
                                                />
                                        </PanelHeader>
                                        <p className="hp-panel__note">
                                                {dataTypeChoice === "SP" ? "Average entrants and splits per race this week." : "Corners per incident. Higher is safer."}
                                        </p>
                                        {dataTypeChoice === "SP" ? (
                                                <div className="hp-chart hp-chart--clickable" onClick={handleViewMore}>
                                                        <Plot
                                                                data={[
                                                                        {
                                                                                type: "bar",
                                                                                x: pageData.repetitions,
                                                                                y: plotlySeasonNames,
                                                                                orientation: "h",
                                                                                name: window.innerWidth > 800 ? "Avg splits" : "Splits",
                                                                                marker: { color: CHART.yellow },
                                                                                hovertemplate:
                                                                                        window.innerWidth > 800
                                                                                                ? "<i>Average # of splits</i>: %{x}<extra></extra>"
                                                                                                : "<i>Splits</i>: %{x}<extra></extra>",
                                                                        },
                                                                        {
                                                                                type: "bar",
                                                                                x: pageData.populations,
                                                                                y: plotlySeasonNames,
                                                                                orientation: "h",
                                                                                name: window.innerWidth > 800 ? "Avg race entrants" : "Entrants",
                                                                                marker: { color: CHART.blue },
                                                                                hovertemplate:
                                                                                        window.innerWidth > 800
                                                                                                ? "<i>Average race entrants</i>: %{x}<extra></extra>"
                                                                                                : "<i>Entrants</i>: %{x}<br><b>%{y}</b><extra></extra>",
                                                                        },
                                                                ]}
                                                                layout={{
                                                                        ...chartBase(),
                                                                        // homePopMargin also sizes the series icons (homePlotWidth/homePlotHeight above).
                                                                        margin: homePopMargin,
                                                                        images: images,
                                                                        barmode: "stack",
                                                                        bargap: 0.32,
                                                                        xaxis: { ...chartAxis(), showgrid: true },
                                                                        yaxis: {
                                                                                ...chartAxis(),
                                                                                autorange: false,
                                                                                range: [homeRowCount - 0.5, -0.5],
                                                                                showticklabels: false,
                                                                                showgrid: false,
                                                                        },
                                                                        legend: chartLegend({ x: 0.99, y: 0.02, xanchor: "right", yanchor: "bottom" }),
                                                                        annotations: [chartWatermark("bottom-right", { y: 0.02, yshift: window.innerWidth < 900 ? 32 : 50 })],
                                                                        width: window.innerWidth > 800 ? 800 : 318,
                                                                        height: window.innerWidth > 800 ? 350 : Math.max(260, Math.floor((window.innerHeight - 420) * 0.85)),
                                                                }}
                                                                config={{ displayModeBar: false }}
                                                        />
                                                        {chart1isLoading && <LoadingOverlay />}
                                                </div>
                                        ) : (
                                                <div className="hp-chart">
                                                        <Plot
                                                                data={[
                                                                        {
                                                                                x:
                                                                                        window.innerWidth > 800
                                                                                                ? dataSS.slice(0, 12).map((item) => trimSafetyName(item.season_name))
                                                                                                : dataSS.slice(0, 8).map((item) => (item.totalCorners / item.totalIncidents).toFixed(2)),
                                                                                y:
                                                                                        window.innerWidth > 800
                                                                                                ? dataSS.slice(0, 12).map((item) => (item.totalCorners / item.totalIncidents).toFixed(2))
                                                                                                : dataSS.slice(0, 8).map((item) => trimSafetyName(item.season_name)),
                                                                                type: "bar",
                                                                                orientation: window.innerWidth > 800 ? "v" : "h",
                                                                                marker: { color: CHART.blue },
                                                                                hoverinfo: window.innerWidth > 800 ? "y" : "x",
                                                                        },
                                                                ]}
                                                                layout={{
                                                                        ...chartBase(),
                                                                        margin: window.innerWidth > 800 ? { l: 40, r: 16, t: 8, b: 70 } : { l: window.innerWidth / 5, r: 10, t: 8, b: 34 },
                                                                        width: window.innerWidth > 800 ? 800 : 318,
                                                                        height: window.innerWidth > 800 ? 350 : Math.max(260, Math.floor((window.innerHeight - 200) * 0.85)),
                                                                        bargap: 0.32,
                                                                        xaxis: { ...chartAxis(), showticklabels: false, showgrid: false },
                                                                        annotations: [chartWatermark(window.innerWidth > 800 ? "top-right" : "bottom-right")],
                                                                        yaxis: {
                                                                                ...chartAxis(),
                                                                                showticklabels: window.innerWidth > 800,
                                                                                autorange: window.innerWidth > 800 ? true : "reversed",
                                                                        },
                                                                        images:
                                                                                window.innerWidth > 800
                                                                                        ? dataSS.map((item) => ({
                                                                                                  source: `${process.env.PUBLIC_URL}/assets/series-icons/${getIconFileName(item.season_name)}.png`,
                                                                                                  xref: "x",
                                                                                                  yref: "paper",
                                                                                                  x: trimSafetyName(item.season_name),
                                                                                                  y: -0.05,
                                                                                                  xanchor: "center",
                                                                                                  yanchor: "top",
                                                                                                  sizex: 0.65,
                                                                                                  sizey: 0.12,
                                                                                                  sizing: "contain",
                                                                                                  layer: "above",
                                                                                          }))
                                                                                        : dataSS.slice(0, 8).map((item) => ({
                                                                                                  source: `${process.env.PUBLIC_URL}/assets/series-icons/${getIconFileName(item.season_name)}.png`,
                                                                                                  xref: "paper",
                                                                                                  yref: "y",
                                                                                                  x: 0.005,
                                                                                                  y: trimSafetyName(item.season_name),
                                                                                                  xanchor: "right",
                                                                                                  yanchor: "middle",
                                                                                                  sizex: 50 / Math.max(1, 318 - window.innerWidth / 5 - 10),
                                                                                                  sizey: 0.55,
                                                                                                  sizing: "contain",
                                                                                                  layer: "above",
                                                                                          })),
                                                                }}
                                                                config={{ displayModeBar: false }}
                                                        />
                                                        {dataSS.length === 0 && <LoadingOverlay />}
                                                </div>
                                        )}
                                </section>

                                {/* Active Contest Section - Hidden */}
                                {false && activeContest && activeContest.status === "active" && (
                                        <section className="hp-panel">
                                                <PanelHeader
                                                        title={`Active contest: ${activeContest.name}`}
                                                        action={
                                                                <Link to="/contests" className="hp-link">
                                                                        View contest
                                                                </Link>
                                                        }
                                                />
                                                {contestLoading ? (
                                                        <div className="hp-board__loading">
                                                                <div className="spinner"></div>
                                                        </div>
                                                ) : (
                                                        <>
                                                                <p className="hp-panel__note">
                                                                        Prize: $20 iRacing credit.{" "}
                                                                        {new Date(activeContest.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })} to{" "}
                                                                        {new Date(activeContest.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}. Time left:{" "}
                                                                        <CountdownTimer endDate={activeContest.endDate} compact={true} />
                                                                </p>
                                                                {activeContest.standings && activeContest.standings.length > 0 ? (
                                                                        <ol className="hp-board">
                                                                                {activeContest.standings.slice(0, 3).map((entry, index) => (
                                                                                        <li key={entry.driverCustId || index}>
                                                                                                <button
                                                                                                        type="button"
                                                                                                        className={`hp-board__row hp-board__row--p${entry.rank}`}
                                                                                                        disabled={!entry.telemetryLapId}
                                                                                                        onClick={() =>
                                                                                                                navigate("/telemetry/compare", {
                                                                                                                        state: {
                                                                                                                                lap: { lapId: entry.telemetryLapId, lapTime: entry.value, lapNumber: entry.lapNumber || 1 },
                                                                                                                                session: { car: entry.car, track: entry.track, driverName: entry.driverName },
                                                                                                                        },
                                                                                                                })
                                                                                                        }
                                                                                                >
                                                                                                        <span className="hp-board__pos">{entry.rank}</span>
                                                                                                        <span className="hp-board__driver">
                                                                                                                <span className="hp-board__name">{entry.driverName}</span>
                                                                                                                <span className="hp-board__split">
                                                                                                                        {entry.car} at {entry.track}
                                                                                                                </span>
                                                                                                        </span>
                                                                                                        <span className="hp-board__ir">{formatContestTime(entry.value)}</span>
                                                                                                </button>
                                                                                        </li>
                                                                                ))}
                                                                        </ol>
                                                                ) : (
                                                                        <p className="hp-panel__note">No entries yet. Set the first time.</p>
                                                                )}
                                                        </>
                                                )}
                                        </section>
                                )}

                                <section className="hp-panel">
                                        <PanelHeader
                                                title="Qualifying pace by iRating"
                                                action={
                                                        <Link to="/charts/SeriesLapTimeChart/" className="hp-link">
                                                                All series
                                                        </Link>
                                                }
                                        >
                                                <SegmentedControl
                                                        label="Series"
                                                        variant="logos"
                                                        value={graphSeriesChoice}
                                                        onChange={setGraphSeriesChoice}
                                                        options={homepageSeries.map((series) => ({
                                                                value: series,
                                                                title: series,
                                                                content: <HomepageSeriesIcon src={getIconUrl(series)} label="" height={window.innerWidth > 800 ? 1.6 : 1.4} />,
                                                        }))}
                                                />
                                        </PanelHeader>
                                        <div className="hp-chart hp-chart--clickable" onClick={handlePlotClick}>
                                                <Plot data={plotData()} layout={layoutSC} config={{ displayModeBar: false, staticPlot: true }} />
                                                {lapLoading && <LoadingOverlay />}
                                                {!lapLoading && dataSG.length === 0 && (
                                                        <div className="hp-empty">No lap times for this series yet this week. Pick another series or open the full chart.</div>
                                                )}
                                        </div>
                                </section>

                                <section className="hp-panel">
                                        <PanelHeader
                                                title="Top iRating"
                                                action={
                                                        <Link to="/leaderboards" className="hp-link">
                                                                Full leaderboard
                                                        </Link>
                                                }
                                        >
                                                <CategoryFilter
                                                        label="Leaderboard category"
                                                        value={topIRatingsCategory}
                                                        onChange={setTopIRatingsCategory}
                                                        options={[
                                                                { value: "combined", title: "Combined", content: "Combined" },
                                                                ...CATEGORIES.map((cat) => ({
                                                                        value: cat,
                                                                        title: CATEGORY_LABELS[cat],
                                                                        content: <CategoryIcon category={cat} size={16} />,
                                                                })),
                                                        ]}
                                                />
                                        </PanelHeader>
                                        <IracingPrivacyNotice />
                                        {topIRatingsLoading ? (
                                                <div className="hp-board__loading">
                                                        <div className="spinner"></div>
                                                </div>
                                        ) : (
                                                <ol className="hp-board">
                                                        {topIRatings.top_100?.slice(0, 5).map((driver, index) => {
                                                                const visible = isVisibleDriver(driver.cust_id);
                                                                return (
                                                                        <li key={driver.cust_id ?? `rank-${driver.rank}-${index}`}>
                                                                                <button
                                                                                        type="button"
                                                                                        className={`hp-board__row hp-board__row--p${index + 1}`}
                                                                                        disabled={!visible}
                                                                                        title={visible ? undefined : "This driver hasn't chosen to share their name"}
                                                                                        onClick={() => visible && navigate(`/user/careerstats/${driver.cust_id}`)}
                                                                                >
                                                                                        <span className="hp-board__pos">{driver.rank}</span>
                                                                                        <span className="hp-board__driver">
                                                                                                <span className="hp-board__name">{maskDriverName(driver.display_name)}</span>
                                                                                                {topIRatingsCategory === "combined" && driver.breakdown && (
                                                                                                        <span className="hp-board__split">
                                                                                                                {CATEGORIES.map((cat) => (
                                                                                                                        <span key={cat} className="hp-board__cat" title={CATEGORY_LABELS[cat]}>
                                                                                                                                <CategoryIcon category={cat} size={12} />
                                                                                                                                {(driver.breakdown[cat] || 0).toLocaleString()}
                                                                                                                        </span>
                                                                                                                ))}
                                                                                                        </span>
                                                                                                )}
                                                                                        </span>
                                                                                        <span className="hp-board__ir">
                                                                                                {driver.irating?.toLocaleString()}
                                                                                                <span className="hp-board__unit">iR</span>
                                                                                        </span>
                                                                                </button>
                                                                        </li>
                                                                );
                                                        })}
                                                </ol>
                                        )}
                                </section>

                                <section className="hp-panel">
                                        <PanelHeader
                                                title="World records"
                                                action={
                                                        <Link to="/worldrecords" className="hp-link">
                                                                Search records
                                                        </Link>
                                                }
                                        />
                                        <p className="hp-panel__note">Recently searched</p>
                                        <div className="hp-wr">
                                                {recentWorldRecords.map((search, index) => (
                                                        <Link key={index} to={`/world-records/${search.carId}/${search.trackId}`} className="hp-wr__item">
                                                                <span className="hp-wr__car">{search.carName}</span>
                                                                <span className="hp-wr__track">{search.trackName}</span>
                                                        </Link>
                                                ))}
                                        </div>
                                </section>

                                <section className="hp-panel">
                                        <PanelHeader
                                                title="iRating distribution"
                                                action={
                                                        <button type="button" className="hp-link" onClick={handleIRatingGraphClick}>
                                                                See more
                                                        </button>
                                                }
                                        >
                                                <CategoryFilter
                                                        label="iRating category"
                                                        value={iRatingCategory}
                                                        onChange={setIRatingCategory}
                                                        options={CATEGORIES.map((cat) => ({
                                                                value: IRATING_CATEGORY_KEYS[cat],
                                                                title: CATEGORY_LABELS[cat],
                                                                content: <CategoryIcon category={cat} size={16} />,
                                                        }))}
                                                />
                                        </PanelHeader>
                                        <div className="hp-chart hp-chart--clickable" onClick={handleIRatingGraphClick}>
                                                <Plot
                                                        data={[
                                                                {
                                                                        x: Object.keys(groupIRatingsByRange(iRatingData)).map((range) => parseInt(range, 10) + 50),
                                                                        y: Object.values(groupIRatingsByRange(iRatingData)),
                                                                        type: "bar",
                                                                        marker: { color: CHART.blue },
                                                                        hovertemplate: "%{y} drivers<extra></extra>",
                                                                },
                                                        ]}
                                                        layout={{
                                                                ...chartBase(),
                                                                margin: window.innerWidth > 800 ? { l: 52, r: 16, t: 8, b: 48 } : { l: 34, r: 8, t: 8, b: 34 },
                                                                bargap: 0.12,
                                                                xaxis: {
                                                                        ...chartAxis(),
                                                                        title: { text: "iRating", font: { size: window.innerWidth < 800 ? 10 : 13, color: CHART.muted }, standoff: 6 },
                                                                        tick0: 0,
                                                                        dtick: window.innerWidth > 800 ? 500 : 1000,
                                                                        showgrid: false,
                                                                        range: window.innerWidth > 800 ? [0, 5000] : [0, 3000],
                                                                },
                                                                yaxis: { ...chartAxis(), tickformat: "~s" },
                                                                annotations: [chartWatermark("top-right")],
                                                                width: window.innerWidth > 800 ? 800 : 318,
                                                                height: window.innerWidth > 800 ? 320 : Math.max(240, Math.floor((window.innerHeight - 420) * 0.6)),
                                                                autosize: false,
                                                        }}
                                                        config={{ displayModeBar: false }}
                                                />
                                                {iRatingLoading && <LoadingOverlay />}
                                        </div>
                                </section>
                        </main>
                </div>
        );
};
